import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { BuildInfo } from './components/layout/BuildInfo';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Monitors } from './pages/Monitors';
import { MonitorDetail } from './pages/MonitorDetail';
import { Incidents } from './pages/Incidents';
import { StatusPages } from './pages/StatusPages';
import { Integrations } from './pages/Integrations';
import { Settings } from './pages/Settings';
import { Plans } from './pages/Plans';
import { Pricing } from './pages/Pricing';
import { Admin } from './pages/Admin';
import { Maintenance } from './pages/Maintenance';
import { PublicStatusPage } from './pages/PublicStatusPage';
import { Help } from './pages/Help';
import { useAuth } from './hooks/useAuth';

// Swagger UI and its stylesheet are larger than the rest of the application
// together. Loading the reference on demand keeps them out of the bundle every
// other page pays for — including the public status pages, which are served to
// a customer's users during an outage.
const ApiDocs = React.lazy(() =>
  import('./pages/ApiDocs').then(module => ({ default: module.ApiDocs }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000,
      retry: 1,
    },
  },
});

function ProtectedRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout />;
}

/**
 * The admin console is gated server-side; this only decides whether to render
 * it, so a non-admin who guesses the URL gets a message rather than a blank
 * page full of failed requests.
 */
function AdminRoute() {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="spinner" />;
  }
  if (!isAdmin) {
    return (
      <div className="empty-state">
        <p>You do not have access to the admin console.</p>
      </div>
    );
  }
  return <Admin />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/status/:slug" element={<PublicStatusPage />} />
          {/* Where a status page served on its own domain lands: the hostname
              identifies it, so no slug is needed in the path. */}
          <Route path="/status" element={<PublicStatusPage byHost />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/*" element={<ProtectedRoutes />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="monitors" element={<Monitors />} />
            <Route path="monitors/:id" element={<MonitorDetail />} />
            <Route path="incidents" element={<Incidents />} />
            <Route path="status-pages" element={<StatusPages />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="settings" element={<Settings />} />
            <Route path="plans" element={<Plans />} />
            <Route path="admin" element={<AdminRoute />} />
            {/* Help is inside the application shell so the sidebar stays put:
                reading about monitors and going back to them should not feel
                like leaving. */}
            <Route path="help" element={<Help />} />
            <Route path="help/:topic" element={<Help />} />
            <Route
              path="api-docs"
              element={
                <React.Suspense fallback={<div className="loading-center"><div className="spinner" /></div>}>
                  <ApiDocs />
                </React.Suspense>
              }
            />
          </Route>
        </Routes>
        <BuildInfo />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
